import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import logo from "../assets/spacem8-logo.png";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { normalizeRole } from "../lib/permissions";
import { logActivity } from "../lib/auditLog";
import {
  ArrowUpRight,
  BrainCircuit,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  FileText,
  House,
  Mail,
  MessageSquareMore,
  Moon,
  MoreHorizontal,
  Plus,
  Printer,
  Search,
  Send,
  Settings,
  Sparkles,
  Sun,
  Users,
  Wallet,
  Wrench,
  X,
} from "lucide-react";

function Button({ children, className = "", variant = "default", ...props }) {
  const base =
    "inline-flex items-center justify-center whitespace-nowrap transition-all font-medium disabled:pointer-events-none disabled:opacity-50";

  const styles =
    variant === "outline"
      ? "border"
      : "bg-orange-500 text-white hover:bg-orange-400 shadow-sm";

  return (
    <button className={`${base} ${styles} ${className}`} {...props}>
      {children}
    </button>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-3 py-2 text-sm outline-none focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

const navItems = [
  { label: "Dashboard", icon: House, path: "/" },
  { label: "Properties", icon: Building2, path: "/properties" },
  { label: "Tenants", icon: Users, path: "/tenants" },
  { label: "Payments", icon: Wallet, path: "/payments" },
  { label: "Invoices", icon: FileText, path: "/invoices" },
  { label: "Maintenance", icon: Wrench, path: "/maintenance" },
  { label: "Messages", icon: MessageSquareMore, path: "/messages" },
  { label: "Reports", icon: ChartNoAxesCombined, path: "/reports" },
  { label: "AI Assistant", icon: BrainCircuit, path: "/ai-assistant" },
  { label: "Settings", icon: Settings, path: "/settings" },
];

function buildInvoiceId(invoiceCount) {
  const year = new Date().getFullYear();
  const sequence = String(invoiceCount + 1).padStart(3, "0");

  return `INV-${year}-${sequence}`;
}

function mapInvoiceFromSupabase(invoice) {
  const amount = Number(invoice.amount || 0);
  const paidAmount = Number(invoice.paidAmount || 0);

  return {
    id: invoice.id,
    tenant: invoice.tenant,
    property: invoice.property,
    unit: invoice.unit,
    amount,
    paidAmount,
    balanceAmount: Math.max(amount - paidAmount, 0),
    issueDate: invoice.issue_date,
    dueDate: invoice.due_date,
    status: invoice.status,
    delivery: invoice.delivery,
  };
}

function invoiceStatusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Partial") return "bg-sky-100 text-sky-700";
  if (status === "Outstanding") return "bg-amber-100 text-amber-800";
  return "bg-rose-100 text-rose-700";
}

function deliveryStatusClasses(status) {
  if (status === "Viewed") return "bg-sky-100 text-sky-700";
  if (status === "Sent") return "bg-indigo-100 text-indigo-700";
  return "bg-slate-200 text-slate-700";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function getLogoUrl() {
  if (/^https?:\/\//i.test(logo)) return logo;
  return `${window.location.origin}${logo.startsWith("/") ? "" : "/"}${logo}`;
}

function getInvoiceHtml(invoice, currency) {
  const balanceLabel = invoice.status === "Paid" ? "Paid in full" : "Amount due";
  const logoUrl = getLogoUrl();

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(invoice.id)}</title>
  <style>
    body { margin: 0; background: #f8fafc; color: #0f172a; font-family: Arial, sans-serif; }
    .invoice { max-width: 820px; margin: 32px auto; background: #fff; padding: 48px; border: 1px solid #e2e8f0; }
    .top { display: flex; justify-content: space-between; gap: 32px; border-bottom: 2px solid #0f172a; padding-bottom: 24px; }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand img { width: 76px; height: auto; object-fit: contain; }
    h1 { margin: 0; font-size: 34px; letter-spacing: 0.04em; }
    h2 { margin: 0 0 8px; font-size: 16px; text-transform: uppercase; letter-spacing: 0.12em; color: #64748b; }
    p { margin: 4px 0; line-height: 1.5; }
    .muted { color: #64748b; }
    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 32px; }
    table { width: 100%; border-collapse: collapse; margin-top: 36px; }
    th { background: #f1f5f9; color: #475569; text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: 0.08em; padding: 14px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 16px 14px; }
    .right { text-align: right; }
    .total { display: flex; justify-content: flex-end; margin-top: 28px; }
    .total-box { min-width: 280px; border: 1px solid #e2e8f0; padding: 18px; }
    .total-row { display: flex; justify-content: space-between; gap: 24px; font-size: 18px; font-weight: 700; }
    .footer { margin-top: 42px; border-top: 1px solid #e2e8f0; padding-top: 20px; color: #64748b; font-size: 13px; }
    @media print { body { background: #fff; } .invoice { margin: 0; max-width: none; border: 0; } }
  </style>
</head>
<body>
  <main class="invoice">
    <section class="top">
      <div class="brand">
        <img src="${escapeHtml(logoUrl)}" alt="SpaceM8" />
        <div>
          <h1>INVOICE</h1>
          <p class="muted">SpaceM8 Property Management</p>
        </div>
      </div>
      <div class="right">
        <p><strong>${escapeHtml(invoice.id)}</strong></p>
        <p>Issued: ${escapeHtml(invoice.issueDate)}</p>
        <p>Due: ${escapeHtml(invoice.dueDate)}</p>
      </div>
    </section>

    <section class="grid">
      <div>
        <h2>Bill To</h2>
        <p><strong>${escapeHtml(invoice.tenant)}</strong></p>
        <p>${escapeHtml(invoice.property)}</p>
        <p>Unit ${escapeHtml(invoice.unit)}</p>
      </div>
      <div>
        <h2>Status</h2>
        <p>${escapeHtml(invoice.status)}</p>
        <p class="muted">Delivery: ${escapeHtml(invoice.delivery)}</p>
      </div>
    </section>

    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Rent and property charges for ${escapeHtml(invoice.property)}, Unit ${escapeHtml(invoice.unit)}</td>
          <td class="right">${escapeHtml(formatMoney(invoice.amount, currency))}</td>
        </tr>
      </tbody>
    </table>

    <section class="total">
      <div class="total-box">
        <p class="muted">${balanceLabel}</p>
        <div class="total-row">
          <span>Total</span>
          <span>${escapeHtml(formatMoney(invoice.amount, currency))}</span>
        </div>
      </div>
    </section>

    <p class="footer">Thank you. Please reference ${escapeHtml(invoice.id)} when making payment.</p>
  </main>
</body>
</html>`;
}

function printInvoice(invoice, currency, onBlocked = () => {}) {
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) {
    onBlocked();
    return;
  }

  printWindow.document.write(getInvoiceHtml(invoice, currency));
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

function downloadInvoice(invoice, currency) {
  const blob = new Blob([getInvoiceHtml(invoice, currency)], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `${invoice.id}.html`;
  link.click();
  URL.revokeObjectURL(url);
}

function extractOrganizationId(profile, user, sessionClaims) {
  return (
    profile?.organization_id ||
    user?.app_metadata?.organization_id ||
    user?.app_metadata?.organizationId ||
    user?.user_metadata?.organization_id ||
    user?.user_metadata?.organizationId ||
    sessionClaims?.app_metadata?.organization_id ||
    sessionClaims?.app_metadata?.organizationId ||
    sessionClaims?.user_metadata?.organization_id ||
    sessionClaims?.user_metadata?.organizationId ||
    ""
  );
}

function emailInvoice(invoice, currency, tenantEmail = "") {
  const subject = encodeURIComponent(`Invoice ${invoice.id} from SpaceM8`);
  const body = encodeURIComponent(
    `Hello ${invoice.tenant},\n\nPlease find your invoice details below.\n\nInvoice: ${invoice.id}\nProperty: ${invoice.property}\nUnit: ${invoice.unit}\nAmount: ${formatMoney(invoice.amount, currency)}\nIssue Date: ${invoice.issueDate}\nDue Date: ${invoice.dueDate}\n\nThank you,\nSpaceM8`
  );

  window.location.href = `mailto:${encodeURIComponent(tenantEmail)}?subject=${subject}&body=${body}`;
}

function Sidebar({ isDark }) {
  return (
    <aside
      className={`hidden xl:flex xl:w-72 xl:flex-col xl:rounded-[2rem] xl:border xl:p-5 xl:shadow-sm ${
        isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
      }`}
    >
      <div className="mb-6 px-2">
        <div
          className={`flex items-center justify-center rounded-3xl border px-6 py-6 shadow-sm transition-all hover:shadow-md ${
            isDark
              ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
              : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
          }`}
        >
          <img
            src={logo}
            alt="SpaceM8"
            className="h-20 w-auto cursor-pointer object-contain transition-all duration-300 hover:scale-110"
            onClick={() => (window.location.href = "/")}
          />
        </div>

        <p className={`mt-4 text-center text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-600"}`}>
          Where Property Meets Intelligence
        </p>
      </div>

      <nav className="mt-1 space-y-2">
        {navItems.map((item) => {
          const Icon = item.icon;

          return (
            <NavLink
              key={item.label}
              to={item.path}
              className={({ isActive }) =>
                `flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition-all ${
                  isActive
                    ? isDark
                      ? "bg-slate-800 text-white ring-1 ring-slate-700"
                      : "bg-sky-50 text-slate-900 ring-1 ring-sky-100"
                    : isDark
                    ? "text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "text-slate-600 hover:bg-gradient-to-br hover:from-white hover:to-sky-50 hover:text-slate-900"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={`h-5 w-5 ${
                      isActive
                        ? isDark
                          ? "text-sky-400"
                          : "text-sky-700"
                        : isDark
                        ? "text-slate-500"
                        : "text-slate-400"
                    }`}
                  />
                  <span className="text-sm font-medium">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div
        className={`mt-6 rounded-3xl border p-5 ${
          isDark
            ? "border-slate-800 bg-gradient-to-br from-slate-900 to-slate-800"
            : "border-slate-200 bg-gradient-to-br from-white to-sky-50"
        }`}
      >
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
          Invoice Summary
        </p>
        <div className="mt-4 space-y-4">
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Billing cycle</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>April 2026</p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Outstanding follow-up</p>
            <p className={`text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>2 invoices</p>
          </div>
          <div>
            <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>Attention flag</p>
            <p className="text-lg font-semibold text-sky-600">Urban Crest Apartments</p>
          </div>
        </div>
      </div>
    </aside>
  );
}

function InvoiceDocumentModal({ isOpen, invoice, tenantEmail, onClose, isDark, currency }) {
  const toast = useToast();
  if (!isOpen || !invoice) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Invoice {invoice.id}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Preview, print, save, or email this invoice.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className={`rounded-3xl border p-6 ${isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"}`}>
          <div className={`flex flex-col gap-6 border-b pb-6 md:flex-row md:items-start md:justify-between ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className="flex items-center gap-4">
              <img src={logo} alt="SpaceM8" className="h-14 w-auto object-contain" />
              <div>
                <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  SpaceM8 Property Management
                </p>
                <h3 className={`mt-3 text-4xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  Invoice
                </h3>
              </div>
            </div>

            <div className={`text-sm md:text-right ${isDark ? "text-slate-300" : "text-slate-600"}`}>
              <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{invoice.id}</p>
              <p>Issued: {invoice.issueDate}</p>
              <p>Due: {invoice.dueDate}</p>
            </div>
          </div>

          <div className="mt-6 grid gap-5 md:grid-cols-2">
            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Bill To
              </p>
              <p className={`mt-2 text-lg font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {invoice.tenant}
              </p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>{invoice.property}</p>
              <p className={isDark ? "text-slate-300" : "text-slate-600"}>Unit {invoice.unit}</p>
            </div>

            <div>
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Status
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Badge className={invoiceStatusClasses(invoice.status)}>{invoice.status}</Badge>
                <Badge className={deliveryStatusClasses(invoice.delivery)}>{invoice.delivery}</Badge>
              </div>
            </div>
          </div>

          <div className={`mt-8 overflow-hidden rounded-2xl border ${isDark ? "border-slate-800" : "border-slate-200"}`}>
            <div className={`grid grid-cols-[1fr_auto] px-5 py-3 text-xs font-semibold uppercase tracking-wide ${isDark ? "bg-slate-900 text-slate-400" : "bg-slate-50 text-slate-500"}`}>
              <span>Description</span>
              <span>Amount</span>
            </div>
            <div className={`grid grid-cols-[1fr_auto] gap-4 border-t px-5 py-5 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-700"}`}>
              <span>Rent and property charges for {invoice.property}, Unit {invoice.unit}</span>
              <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                {formatMoney(invoice.amount, currency)}
              </span>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <div className={`w-full max-w-sm rounded-2xl border p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50"}`}>
              <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                {invoice.status === "Paid" ? "Paid in full" : "Amount due"}
              </p>
              <div className={`mt-2 flex items-center justify-between text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                <span>Total</span>
                <span>{formatMoney(invoice.amount, currency)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <Button
            variant="outline"
            onClick={() =>
              printInvoice(
                invoice,
                currency,
                () =>
                  toast.error(
                    "Invoice could not open",
                    "Please allow pop-ups to print or save this invoice."
                  )
              )
            }
            className={`rounded-2xl px-4 py-3 text-sm ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <Printer className="mr-2 h-4 w-4" />
            Print / Save PDF
          </Button>

          <Button
            variant="outline"
            onClick={() => downloadInvoice(invoice, currency)}
            className={`rounded-2xl px-4 py-3 text-sm ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <FileText className="mr-2 h-4 w-4" />
            Save File
          </Button>

          <Button
            onClick={() => emailInvoice(invoice, currency, tenantEmail)}
            className="rounded-2xl px-4 py-3 text-sm"
          >
            <Mail className="mr-2 h-4 w-4" />
            Email Invoice
          </Button>
        </div>
      </div>
    </div>
  );
}

function InvoiceRow({ invoice, index, isDark, onOpen, currency }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className={`grid items-center gap-4 border-t px-5 py-4 text-sm ${
        isDark ? "border-slate-800" : "border-slate-200"
      } md:grid-cols-[1.3fr_0.9fr_0.85fr_0.85fr_0.85fr_0.75fr_44px]`}
    >
      <div>
        <p className={`font-medium ${isDark ? "text-white" : "text-slate-900"}`}>{invoice.tenant}</p>
        <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          {invoice.property} • {invoice.unit} • {invoice.id}
        </p>
      </div>

      <div>
        <p className={isDark ? "text-slate-300" : "text-slate-700"}>{formatMoney(invoice.amount, currency)}</p>
        <p className={`mt-1 text-xs ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          Balance {formatMoney(invoice.balanceAmount, currency)}
        </p>
      </div>
      <div className={isDark ? "text-slate-300" : "text-slate-700"}>{invoice.issueDate}</div>
      <div className={isDark ? "text-slate-300" : "text-slate-700"}>{invoice.dueDate}</div>
      <div>
        <Badge className={invoiceStatusClasses(invoice.status)}>{invoice.status}</Badge>
      </div>
      <div>
        <Badge className={deliveryStatusClasses(invoice.delivery)}>{invoice.delivery}</Badge>
      </div>

      <button
        onClick={() => onOpen(invoice)}
        className={`rounded-xl border p-2 transition ${
          isDark
            ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
            : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"
        }`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </motion.div>
  );
}

export default function SpaceM8InvoicesPage({ isDark, setIsDark, currency }) {
  const location = useLocation();
  const toast = useToast();
  const { profile, user, sessionClaims } = useAuth();
  const [invoiceList, setInvoiceList] = useState([]);
  const [tenantEmails, setTenantEmails] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendingBatch, setSendingBatch] = useState(false);
  const normalizedRole = normalizeRole(
    profile?.role ||
      user?.app_metadata?.role ||
      user?.app_metadata?.app_role ||
      user?.user_metadata?.role ||
      user?.user_metadata?.app_role ||
      sessionClaims?.app_metadata?.role ||
      sessionClaims?.app_metadata?.app_role ||
      sessionClaims?.user_metadata?.role ||
      sessionClaims?.user_metadata?.app_role
  );
  const isGrowthUser = normalizedRole === "Growth";
  const currentOrganizationId = extractOrganizationId(profile, user, sessionClaims);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);

    if (!currentOrganizationId) {
      setInvoiceList([]);
      setLoading(false);
      return;
    }

    let invoicesQuery = supabase
      .from("invoices")
      .select("*")
      .order("created_at", { ascending: false });
    let paymentsQuery = supabase
      .from("payments")
      .select("invoice_id, amount, status");

    if (currentOrganizationId) {
      invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
      paymentsQuery = paymentsQuery.eq("organization_id", currentOrganizationId);
    }

    const [{ data, error }, { data: paymentsData, error: paymentsError }] = await Promise.all([
      invoicesQuery,
      paymentsQuery,
    ]);

    if (error) {
      console.error("Error fetching invoices:", error);
      setInvoiceList([]);
    } else {
      if (paymentsError) {
        console.error("Error fetching invoice payment totals:", paymentsError);
      }

      const paidByInvoice = (paymentsData || []).reduce((totalsByInvoice, payment) => {
        if (payment.invoice_id && payment.status === "Paid") {
          totalsByInvoice[payment.invoice_id] =
            (totalsByInvoice[payment.invoice_id] || 0) + Number(payment.amount || 0);
        }
        return totalsByInvoice;
      }, {});

      setInvoiceList(
        (data || []).map((invoice) =>
          mapInvoiceFromSupabase({
            ...invoice,
            paidAmount: paidByInvoice[invoice.id] || 0,
          })
        )
      );
    }

    setLoading(false);
  }, [currentOrganizationId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    const nextState = location.state;

    if (!nextState) return;

    if (nextState.initialQuery) {
      setQuery(nextState.initialQuery);
    }

    if (nextState.initialStatusFilter) {
      setStatusFilter(nextState.initialStatusFilter);
    }

    window.history.replaceState({}, document.title);
  }, [location.state]);

  useEffect(() => {
    const fetchTenantEmails = async () => {
      if (!currentOrganizationId) {
        setTenantEmails({});
        return;
      }

      let query = supabase
        .from("tenants")
        .select("full_name, email");

      if (currentOrganizationId) {
        query = query.eq("organization_id", currentOrganizationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching tenant emails:", error);
        setTenantEmails({});
        return;
      }

      setTenantEmails(
        (data || []).reduce((emails, tenant) => {
          if (tenant.full_name && tenant.email) {
            emails[tenant.full_name] = tenant.email;
          }
          return emails;
        }, {})
      );
    };

    fetchTenantEmails();
  }, [currentOrganizationId, isGrowthUser]);

  const handleCreateInvoice = async (invoice) => {
    const payload = {
      id: invoice.id,
      tenant: invoice.tenant,
      property: invoice.property,
      unit: invoice.unit,
      amount: invoice.amount,
      issue_date: invoice.issueDate,
      due_date: invoice.dueDate,
      status: invoice.status,
      delivery: invoice.delivery,
      organization_id: currentOrganizationId || null,
    };

    const { data, error } = await supabase
      .from("invoices")
      .insert([payload])
      .select()
      .single();

    if (error) {
      console.error("Create invoice error:", error);
      toast.error("Invoice creation failed", "Please review the invoice details and try again.");
      return false;
    }

    await logActivity({
      action: "Created invoice",
      entityType: "invoice",
      entityId: data.id,
      entityLabel: `${data.tenant} - ${data.id}`,
      details: payload,
    });

    setInvoiceList((prev) => [mapInvoiceFromSupabase(data), ...prev]);
    toast.success("Invoice created", `${data.id} is now saved in SpaceM8.`);
    return true;
  };

  const handleSendBatch = async () => {
    const draftInvoiceIds = invoiceList
      .filter((invoice) => invoice.delivery === "Draft")
      .map((invoice) => invoice.id);

    if (draftInvoiceIds.length === 0) {
      toast.info("No draft invoices", "There are no draft invoices ready to send right now.");
      return;
    }

    setSendingBatch(true);

    const { error } = await supabase
      .from("invoices")
      .update({ delivery: "Sent" })
      .in("id", draftInvoiceIds);

    setSendingBatch(false);

    if (error) {
      console.error("Send invoice batch error:", error);
      toast.error("Invoice batch send failed", "Please try sending the draft invoices again.");
      return;
    }

    await logActivity({
      action: "Sent invoice batch",
      entityType: "invoice",
      entityLabel: `${draftInvoiceIds.length} invoices`,
      details: { invoiceIds: draftInvoiceIds },
    });

    setInvoiceList((prev) =>
      prev.map((invoice) =>
        draftInvoiceIds.includes(invoice.id)
          ? { ...invoice, delivery: "Sent" }
          : invoice
      )
    );
    toast.success("Invoices sent", `${draftInvoiceIds.length} draft invoice(s) were marked as sent.`);
  };

  const handleBulkGenerated = async (createdInvoices) => {
    if (createdInvoices.length > 0) {
      await logActivity({
        action: "Generated monthly rent invoices",
        entityType: "invoice",
        entityLabel: `${createdInvoices.length} invoices`,
        details: {
          invoiceIds: createdInvoices.map((invoice) => invoice.id),
        },
      });
    }

    await fetchInvoices();
  };

  const totals = useMemo(() => {
    const paid = invoiceList
      .reduce((sum, item) => sum + Number(item.paidAmount || 0), 0);
    const outstanding = invoiceList
      .filter((item) => item.status === "Outstanding" || item.status === "Partial")
      .reduce((sum, item) => sum + Number(item.balanceAmount || item.amount || 0), 0);
    const overdue = invoiceList
      .filter((item) => item.status === "Overdue")
      .reduce((sum, item) => sum + Number(item.balanceAmount || item.amount || 0), 0);

    return {
      paid,
      outstanding,
      overdue,
      totalInvoices: invoiceList.length,
    };
  }, [invoiceList]);

  const filteredInvoices = useMemo(() => {
    return invoiceList.filter((invoice) => {
      const matchesQuery =
        invoice.tenant.toLowerCase().includes(query.toLowerCase()) ||
        invoice.property.toLowerCase().includes(query.toLowerCase()) ||
        invoice.unit.toLowerCase().includes(query.toLowerCase()) ||
        invoice.id.toLowerCase().includes(query.toLowerCase());

      const matchesStatus = statusFilter === "All" || invoice.status === statusFilter;

      return matchesQuery && matchesStatus;
    });
  }, [invoiceList, query, statusFilter]);

  return (
    <div
      className={`min-h-screen ${
        isDark
          ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white"
          : "bg-gradient-to-br from-[#f8fbff] via-[#eef6ff] to-[#f3f9ff] text-slate-900"
      }`}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className={`absolute inset-0 ${
            isDark
              ? "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.08),_transparent_22%),linear-gradient(to_bottom_right,_#020617,_#0f172a,_#020617)]"
              : "bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.10),_transparent_24%),linear-gradient(to_bottom_right,_#f8fbff,_#f6f9fc,_#eef5fb)]"
          }`}
        />
      </div>

      <div className="relative mx-auto flex max-w-[1600px] gap-6 p-4 md:p-6 xl:p-8">
        <Sidebar isDark={isDark} />

        <main className="flex-1">
          <motion.header
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`rounded-[2rem] border p-5 shadow-sm ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="grid gap-5 xl:grid-cols-[minmax(320px,1fr)_auto] xl:items-start">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sky-700">
                  <Sparkles className="h-4 w-4" />
                  <p className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Billing Operations
                  </p>
                </div>
                <h1 className={`mt-2 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                  Invoices
                </h1>
                <p className={`mt-2 max-w-xl text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Track issued invoices, delivery status, outstanding balances, and overdue billing activity from one executive workspace.
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-start gap-2 xl:flex-nowrap xl:justify-end">
                <Button
                  variant="outline"
                  className={`h-10 rounded-xl px-3 text-sm ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  onClick={() => setIsDark(!isDark)}
                >
                  {isDark ? <Sun className="mr-2 h-4 w-4" /> : <Moon className="mr-2 h-4 w-4" />}
                  {isDark ? "Light" : "Dark"}
                </Button>

                <Button
                  variant="outline"
                  onClick={handleSendBatch}
                  disabled={sendingBatch}
                  className={`h-10 rounded-xl px-3 text-sm ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Send className="mr-2 h-4 w-4" />
                  {sendingBatch ? "Sending..." : "Send Batch"}
                </Button>

                <Button
                  variant="outline"
                  onClick={() => setIsBulkOpen(true)}
                  className={`h-10 rounded-xl px-3 text-sm ${
                    isDark
                      ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Generate Rent
                </Button>

                <Button
                  onClick={() => setIsCreateOpen(true)}
                  className="h-10 rounded-xl px-3 text-sm"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Invoice
                </Button>
              </div>
            </div>
          </motion.header>

          <section className="mt-6 grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
            {[
              {
                label: "Paid",
                value: formatMoney(totals.paid, currency),
                icon: Wallet,
                tone: "bg-emerald-100 text-emerald-700",
              },
              {
                label: "Outstanding",
                value: formatMoney(totals.outstanding, currency),
                icon: FileText,
                tone: "bg-amber-100 text-amber-800",
              },
              {
                label: "Overdue",
                value: formatMoney(totals.overdue, currency),
                icon: CalendarDays,
                tone: "bg-rose-100 text-rose-700",
              },
              {
                label: "Invoices",
                value: `${totals.totalInvoices}`,
                icon: Mail,
                tone: "bg-sky-100 text-sky-700",
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className={`rounded-[2rem] border p-5 shadow-md transition-shadow hover:shadow-lg ${
                    isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        {item.label}
                      </p>
                      <h3 className={`mt-3 text-3xl font-semibold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                        {item.value}
                      </h3>
                      <div
                        className={`mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm ${
                          isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"
                        }`}
                      >
                        <ArrowUpRight className="h-4 w-4 text-sky-600" />
                        Billing visibility strong
                      </div>
                    </div>
                    <div className={`rounded-2xl p-3 ${item.tone}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </section>

          <section
            className={`mt-6 rounded-[2rem] border p-4 shadow-sm md:p-5 ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-1 flex-col gap-3 md:flex-row md:items-center">
                <div className="relative w-full max-w-md">
                  <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by tenant, property, unit, or invoice ID..."
                    className={`h-12 pl-11 pr-4 ${
                      isDark
                        ? "border-slate-700 bg-slate-900 text-white placeholder:text-slate-400"
                        : "border-slate-200 bg-white text-slate-800 placeholder:text-slate-500"
                    }`}
                  />
                </div>

                <div className="flex items-center gap-2">
                  {["All", "Paid", "Partial", "Outstanding", "Overdue"].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                        statusFilter === status
                          ? "bg-sky-100 text-sky-700"
                          : isDark
                          ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                          : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section
            className={`mt-6 overflow-hidden rounded-[2rem] border shadow-md ${
              isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div
              className={`hidden gap-4 px-5 py-4 text-sm md:grid md:grid-cols-[1.3fr_0.9fr_0.85fr_0.85fr_0.85fr_0.75fr_44px] ${
                isDark ? "bg-slate-900 text-slate-300" : "bg-gradient-to-br from-white to-sky-50 text-slate-600"
              }`}
            >
              <span>Tenant</span>
              <span>Amount</span>
              <span>Issue Date</span>
              <span>Due Date</span>
              <span>Status</span>
              <span>Delivery</span>
              <span></span>
            </div>

            <div>
              {loading ? (
                <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Loading invoices...
                </div>
              ) : filteredInvoices.length === 0 ? (
                <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  No invoices found.
                </div>
              ) : (
                filteredInvoices.map((invoice, index) => (
                  <InvoiceRow
                    key={invoice.id}
                    invoice={invoice}
                    index={index}
                    isDark={isDark}
                    onOpen={setSelectedInvoice}
                    currency={currency}
                  />
                ))
              )}
            </div>
          </section>
        </main>
      </div>

      <CreateInvoiceModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onCreated={handleCreateInvoice}
        isDark={isDark}
        invoiceCount={invoiceList.length}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <BulkRentInvoiceModal
        isOpen={isBulkOpen}
        onClose={() => setIsBulkOpen(false)}
        onGenerated={handleBulkGenerated}
        isDark={isDark}
        invoiceCount={invoiceList.length}
        currency={currency}
        currentOrganizationId={currentOrganizationId}
        isGrowthUser={isGrowthUser}
      />

      <InvoiceDocumentModal
        isOpen={!!selectedInvoice}
        invoice={selectedInvoice}
        tenantEmail={selectedInvoice ? tenantEmails[selectedInvoice.tenant] : ""}
        currency={currency}
        onClose={() => setSelectedInvoice(null)}
        isDark={isDark}
      />
    </div>
  );
}

function CreateInvoiceModal({
  isOpen,
  onClose,
  onCreated,
  isDark,
  invoiceCount,
  currentOrganizationId,
  isGrowthUser,
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    tenantId: "",
    tenant: "",
    property: "",
    unit: "",
    amount: "",
    issueDate: today,
    dueDate: today,
    status: "Outstanding",
    delivery: "Draft",
  });
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      tenantId: "",
      tenant: "",
      property: "",
      unit: "",
      amount: "",
      issueDate: today,
      dueDate: today,
      status: "Outstanding",
      delivery: "Draft",
    });
    setSaving(false);
  }, [isOpen, today]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchTenants = async () => {
      setLoadingTenants(true);

      if (!currentOrganizationId) {
        setTenants([]);
        setLoadingTenants(false);
        return;
      }

      let query = supabase
        .from("tenants")
        .select("id, full_name, property_name, unit, monthly_rent")
        .order("full_name", { ascending: true });

      if (currentOrganizationId) {
        query = query.eq("organization_id", currentOrganizationId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching tenants for invoice:", error);
        setTenants([]);
      } else {
        setTenants(data || []);
      }

      setLoadingTenants(false);
    };

    fetchTenants();
  }, [currentOrganizationId, isGrowthUser, isOpen]);

  if (!isOpen) return null;

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTenantChange = (tenantId) => {
    const selectedTenant = tenants.find((tenant) => tenant.id === tenantId);

    setForm((prev) => ({
      ...prev,
      tenantId,
      tenant: selectedTenant?.full_name || "",
      property: selectedTenant?.property_name || "",
      unit: selectedTenant?.unit || "",
      amount: selectedTenant?.monthly_rent ?? "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);

    const wasCreated = await onCreated({
      id: buildInvoiceId(invoiceCount),
      tenantId: form.tenantId,
      tenant: form.tenant,
      property: form.property,
      unit: form.unit,
      amount: Number(form.amount || 0),
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      status: form.status,
      delivery: form.delivery,
    });

    setSaving(false);

    if (wasCreated) {
      onClose();
    }
  };

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Create Invoice
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Add a billing record to the invoice register.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Select
            value={form.tenantId}
            onChange={(e) => handleTenantChange(e.target.value)}
            className={inputClass}
            required
          >
            <option value="">
              {loadingTenants ? "Loading tenants..." : "Select tenant"}
            </option>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.full_name} - {tenant.property_name} - Unit {tenant.unit}
              </option>
            ))}
          </Select>

          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              isDark
                ? "border-slate-700 bg-slate-950 text-slate-300"
                : "border-slate-200 bg-slate-50 text-slate-600"
            }`}
          >
            Selecting a tenant fills property, unit, and rent automatically.
          </div>

          <Input
            placeholder="Property"
            value={form.property}
            onChange={(e) => update("property", e.target.value)}
            className={inputClass}
            required
          />

          <Input
            placeholder="Unit"
            value={form.unit}
            onChange={(e) => update("unit", e.target.value)}
            className={inputClass}
            required
          />

          <Input
            type="number"
            placeholder="Amount"
            value={form.amount}
            onChange={(e) => update("amount", e.target.value)}
            className={inputClass}
            required
          />

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Issue date
            </label>
            <Input
              type="date"
              value={form.issueDate}
              onChange={(e) => update("issueDate", e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <div>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Due date
            </label>
            <Input
              type="date"
              value={form.dueDate}
              onChange={(e) => update("dueDate", e.target.value)}
              className={inputClass}
              required
            />
          </div>

          <Select
            value={form.status}
            onChange={(e) => update("status", e.target.value)}
            className={inputClass}
          >
            <option>Paid</option>
            <option>Outstanding</option>
            <option>Overdue</option>
          </Select>

          <Select
            value={form.delivery}
            onChange={(e) => update("delivery", e.target.value)}
            className={inputClass}
          >
            <option>Draft</option>
            <option>Sent</option>
            <option>Viewed</option>
          </Select>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Creating..." : "Create Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkRentInvoiceModal({
  isOpen,
  onClose,
  onGenerated,
  isDark,
  invoiceCount,
  currency,
  currentOrganizationId,
  isGrowthUser,
}) {
  const toast = useToast();
  const today = new Date();
  const currentMonth = today.toISOString().slice(0, 7);
  const [month, setMonth] = useState(currentMonth);
  const [issueDay, setIssueDay] = useState("01");
  const [dueDay, setDueDay] = useState("05");
  const [tenants, setTenants] = useState([]);
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const fetchBillingData = async () => {
      setLoading(true);

      if (!currentOrganizationId) {
        setTenants([]);
        setExistingInvoices([]);
        setLoading(false);
        return;
      }

      let tenantsQuery = supabase
        .from("tenants")
        .select("id, full_name, property_name, unit, monthly_rent, status, organization_id")
        .order("full_name", { ascending: true });
      let invoicesQuery = supabase
        .from("invoices")
        .select("id, tenant, property, unit, issue_date");

      if (currentOrganizationId) {
        tenantsQuery = tenantsQuery.eq("organization_id", currentOrganizationId);
        invoicesQuery = invoicesQuery.eq("organization_id", currentOrganizationId);
      }

      const [{ data: tenantData, error: tenantError }, { data: invoiceData, error: invoiceError }] =
        await Promise.all([
          tenantsQuery,
          invoicesQuery,
        ]);

      if (tenantError) {
        console.error("Error fetching tenants for bulk invoices:", tenantError);
        setTenants([]);
      } else {
        setTenants((tenantData || []).filter((tenant) => tenant.status !== "Inactive"));
      }

      if (invoiceError) {
        console.error("Error fetching existing invoices for bulk generation:", invoiceError);
        setExistingInvoices([]);
      } else {
        setExistingInvoices(invoiceData || []);
      }

      setLoading(false);
    };

    fetchBillingData();
  }, [currentOrganizationId, isGrowthUser, isOpen]);

  if (!isOpen) return null;

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";
  const invoiceMonthKey = month.replace("-", "");
  const issueDate = `${month}-${issueDay.padStart(2, "0")}`;
  const dueDate = `${month}-${dueDay.padStart(2, "0")}`;
  const alreadyBilledKeys = new Set(
    existingInvoices
      .filter((invoice) => invoice.issue_date?.slice(0, 7) === month)
      .map((invoice) => `${invoice.tenant}|${invoice.property}|${invoice.unit}`)
  );
  const eligibleTenants = tenants.filter(
    (tenant) =>
      Number(tenant.monthly_rent || 0) > 0 &&
      !alreadyBilledKeys.has(`${tenant.full_name}|${tenant.property_name}|${tenant.unit}`)
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (eligibleTenants.length === 0) {
      toast.info("No eligible tenants", "Every active tenant for this month has already been billed.");
      return;
    }

    setSaving(true);

    const payload = eligibleTenants.map((tenant, index) => {
      const tenantKey = String(tenant.id || `${invoiceCount}-${index}`)
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 8)
        .toUpperCase();

      return {
        id: `INV-${invoiceMonthKey}-${tenantKey || String(invoiceCount + index + 1).padStart(3, "0")}`,
        tenant: tenant.full_name,
        property: tenant.property_name,
        unit: tenant.unit,
        amount: Number(tenant.monthly_rent || 0),
        issue_date: issueDate,
        due_date: dueDate,
        status: "Outstanding",
        delivery: "Draft",
        organization_id: tenant.organization_id || currentOrganizationId || null,
      };
    });

    const { data, error } = await supabase
      .from("invoices")
      .upsert(payload, { onConflict: "id" })
      .select();

    setSaving(false);

    if (error) {
      console.error("Bulk rent invoice generation error:", error);
      toast.error("Bulk invoice generation failed", "Please try generating the rent invoices again.");
      return;
    }

    await onGenerated(data || []);
    toast.success("Rent invoices generated", `${(data || []).length} invoice(s) were created.`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`w-full max-w-3xl rounded-[2rem] border p-6 shadow-2xl ${
          isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"
        }`}
      >
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              Generate Rent Invoices
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Create monthly rent invoices for active tenants who have not been billed for the selected month.
            </p>
          </div>

          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${
              isDark
                ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Billing month
              </label>
              <Input
                type="month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Issue day
              </label>
              <Input
                type="number"
                min="1"
                max="28"
                value={issueDay}
                onChange={(event) => setIssueDay(event.target.value)}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
                Due day
              </label>
              <Input
                type="number"
                min="1"
                max="28"
                value={dueDay}
                onChange={(event) => setDueDay(event.target.value)}
                className={inputClass}
                required
              />
            </div>
          </div>

          <div
            className={`rounded-2xl border p-4 ${
              isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {loading ? "Checking tenants..." : `${eligibleTenants.length} invoices ready`}
                </p>
                <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                  Existing invoices for the same tenant, property, unit, and month are skipped.
                </p>
              </div>
              <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                Draft
              </span>
            </div>
          </div>

          <div className="max-h-72 space-y-2 overflow-y-auto">
            {eligibleTenants.length === 0 ? (
              <div className={`rounded-2xl border p-4 text-sm ${isDark ? "border-slate-800 text-slate-300" : "border-slate-200 text-slate-600"}`}>
                No unbilled active tenants found for {month}.
              </div>
            ) : (
              eligibleTenants.map((tenant) => (
                <div
                  key={tenant.id}
                  className={`flex items-center justify-between gap-4 rounded-2xl border p-4 text-sm ${
                    isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-white"
                  }`}
                >
                  <div>
                    <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                      {tenant.full_name}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {tenant.property_name} - Unit {tenant.unit}
                    </p>
                  </div>
                  <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {formatMoney(Number(tenant.monthly_rent || 0), currency)}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`inline-flex h-12 items-center justify-center rounded-2xl border px-5 font-medium transition ${
                isDark
                  ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || loading || eligibleTenants.length === 0}
              className={`inline-flex h-12 items-center justify-center rounded-2xl px-5 font-medium text-white transition ${
                saving || loading || eligibleTenants.length === 0
                  ? "cursor-not-allowed bg-orange-300"
                  : "bg-orange-500 hover:bg-orange-600"
              }`}
            >
              {saving ? "Generating..." : "Generate Invoices"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
